// Leave rules — the ONE source of truth for the annual-leave allowance.
//
// CommonJS on purpose: webpack imports it into the React portal AND
// netlify/functions/roster-data.js requires it directly, so the browser and the
// server can never disagree about what the rules are.
//
// History (21 Jul 2026): these rules used to live in two hand-copied client
// implementations that had silently drifted — different leave types counted,
// a 364-day window vs a calendar year, case-sensitive name matching, and no
// exclusion for an amendment's own booking. Same driver, same data, two
// different answers, and NOTHING enforced on the server. Consolidated here.
//
// Public/roster-calendar.html still carries an inline copy (it's standalone
// HTML behind the QR code and can't import). That copy is now advisory only —
// the server is the authority, so if it drifts the outcome doesn't change.

// ── NSW public holidays ─────────────────────────────────────────────────────
// A driver can't work Australia Day, so a holiday inside a leave range
// shouldn't consume one of their 20 days. Deliberately SILENT: never tell the
// driver a holiday was skipped, just tally the smaller number (Paul, 21 Jul 2026).
//
// Hand-gazetted through 2030 (no reliable free NSW feed). Weekend dates are
// listed for completeness even though a Mon–Fri count never reaches them; the
// substitute weekday is what actually matters. EXTEND BEFORE JAN 2031.
const NSW_PUBLIC_HOLIDAYS = new Set([
  // 2026
  '2026-01-01', '2026-01-26',
  '2026-04-03', '2026-04-04', '2026-04-05', '2026-04-06', '2026-04-25',
  '2026-06-08', '2026-10-05',
  '2026-12-25', '2026-12-26', '2026-12-28',
  // 2027
  '2027-01-01', '2027-01-26',
  '2027-03-26', '2027-03-27', '2027-03-28', '2027-03-29', '2027-04-25',
  '2027-06-14', '2027-10-04',
  '2027-12-25', '2027-12-26', '2027-12-27', '2027-12-28',
  // 2028
  '2028-01-01', '2028-01-03', '2028-01-26',
  '2028-04-14', '2028-04-15', '2028-04-16', '2028-04-17', '2028-04-25',
  '2028-06-12', '2028-10-02',
  '2028-12-25', '2028-12-26',
  // 2029
  '2029-01-01', '2029-01-26',
  '2029-03-30', '2029-03-31', '2029-04-01', '2029-04-02', '2029-04-25',
  '2029-06-11', '2029-10-01',
  '2029-12-25', '2029-12-26',
  // 2030
  '2030-01-01', '2030-01-26', '2030-01-28',
  '2030-04-19', '2030-04-20', '2030-04-21', '2030-04-22', '2030-04-25',
  '2030-06-10', '2030-10-07',
  '2030-12-25', '2030-12-26',
]);

const ANNUAL_ALLOWANCE = 20;                            // working days per rolling 12 months
const HOLIDAY_TYPES = new Set(['annual', 'exception']); // what eats the allowance
const SICK_TYPES = new Set(['sick']);

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Working days (Mon–Fri, less NSW public holidays) inclusive of both endpoints.
function workingDayCount(start, end) {
  let count = 0;
  const d = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  while (d <= e) {
    const dow = d.getDay(); // 0=Sun, 6=Sat
    if (dow !== 0 && dow !== 6 && !NSW_PUBLIC_HOLIDAYS.has(isoOf(d))) count++;
    d.setDate(d.getDate() + 1);
  }
  return count;
}

// Shift an ISO date by whole months (negative = back). Calendar-correct — the
// old QR page used 364*86400000 ms, which is both a day short of a year and
// liable to land on the wrong date across a DST boundary.
function addMonths(iso, n) {
  const d = new Date(iso + 'T12:00:00');
  d.setMonth(d.getMonth() + n);
  return isoOf(d);
}

// Names arrive from a sheet, a portal session and a hand-typed QR form, so a
// stray space or case difference must not zero someone's tally.
function normName(n) { return (n || '').trim().toLowerCase(); }

// Approved working days for this driver, of the given leave types, overlapping
// a window. excludeId lets an in-progress amendment ignore its own booking.
function approvedWorkingDays(entries, name, types, windowStart, windowEnd, excludeId) {
  const nm = normName(name);
  return (entries || [])
    .filter((e) => e.status === 'approved'
      && e.id !== excludeId
      && normName(e.name) === nm
      && types.has(e.type))
    .reduce((total, e) => {
      const oStart = e.startDate > windowStart ? e.startDate : windowStart;
      const oEnd   = e.endDate   < windowEnd   ? e.endDate   : windowEnd;
      return oEnd >= oStart ? total + workingDayCount(oStart, oEnd) : total;
    }, 0);
}

// The allowance check both clients preview and the server enforces.
// Returns { ok:true, requestDays, used, remaining } or { ok:false, code, message, ... }.
function checkAnnualAllowance(entries, req) {
  const { name, type, startDate, endDate, excludeId } = req;
  if (!startDate || !endDate) return { ok: false, code: 'DATES', message: 'Please choose your start and end dates.' };
  if (endDate < startDate)    return { ok: false, code: 'ORDER', message: 'End date must be on or after the start date.' };

  const requestDays = workingDayCount(startDate, endDate);
  if (requestDays > ANNUAL_ALLOWANCE) {
    return {
      ok: false, code: 'PER_REQUEST', requestDays,
      message: `That’s ${requestDays} working days — the maximum per request is ${ANNUAL_ALLOWANCE} (4 weeks Mon–Fri). Please shorten your dates.`,
    };
  }
  // The allowance covers annual leave only; sick leave isn't capped by it, and
  // an 'exception' is a manager-granted override that's already been judged.
  if (type !== 'annual') return { ok: true, requestDays, used: 0, remaining: ANNUAL_ALLOWANCE };

  const used = approvedWorkingDays(entries, name, HOLIDAY_TYPES, addMonths(startDate, -12), endDate, excludeId);
  const remaining = Math.max(0, ANNUAL_ALLOWANCE - used);
  if (used + requestDays > ANNUAL_ALLOWANCE) {
    return {
      ok: false, code: 'ALLOWANCE', requestDays, used, remaining,
      message: `You’ve used ${used} of your ${ANNUAL_ALLOWANCE} holiday days in the rolling 12 months. This request needs ${requestDays}, leaving you ${remaining}. Please adjust your dates.`,
    };
  }
  return { ok: true, requestDays, used, remaining };
}

// ── Server-side gate ────────────────────────────────────────────────────────
// roster-data's POST replaces the WHOLE dataset, so the only way to police it
// is to diff the incoming payload against what's stored and judge what changed.
//
// Managers and admins bypass everything: granting leave over the allowance is a
// legitimate call they must keep (Paul authorised exactly that for YINGM3 on
// 21 Jul 2026, because Australia Day fell in the range and the driver had taken
// one day all year). This gate exists to stop a DRIVER editing the numbers.
//
// Returns { ok:true } or { ok:false, status, error }.
function validateRosterWrite(prevEntries, nextEntries, opts) {
  const isManager = !!(opts && opts.isManager);
  if (isManager) return { ok: true };
  if (!Array.isArray(nextEntries)) return { ok: false, status: 400, error: 'entries must be an array' };

  const prev = new Map((prevEntries || []).map((e) => [e.id, e]));

  for (const e of nextEntries) {
    const before = prev.get(e.id);

    // A driver may only ever create a PENDING booking — never approve their own.
    if (!before) {
      if (e.status !== 'pending') {
        return { ok: false, status: 403, error: 'New bookings must be submitted as pending for manager approval.' };
      }
      const v = checkAnnualAllowance(prevEntries, {
        name: e.name, type: e.type, startDate: e.startDate, endDate: e.endDate,
      });
      if (!v.ok) return { ok: false, status: 409, error: v.message };
      continue;
    }

    // ...nor change the status of an existing one (approve, un-reject, revive).
    if (e.status !== before.status) {
      return { ok: false, status: 403, error: 'Only a manager can change the status of a booking.' };
    }

    // An amendment keeps the booking approved but proposes new dates; those
    // dates have to fit the allowance too, ignoring the booking's own days.
    const ch = e.change;
    const chBefore = before.change;
    const changed = ch && ch.kind === 'amend'
      && (!chBefore || chBefore.newStart !== ch.newStart || chBefore.newEnd !== ch.newEnd);
    if (changed) {
      const v = checkAnnualAllowance(prevEntries, {
        name: e.name, type: e.type, startDate: ch.newStart, endDate: ch.newEnd, excludeId: e.id,
      });
      if (!v.ok) return { ok: false, status: 409, error: v.message };
    }
  }
  return { ok: true };
}

module.exports = {
  NSW_PUBLIC_HOLIDAYS,
  ANNUAL_ALLOWANCE,
  HOLIDAY_TYPES,
  SICK_TYPES,
  workingDayCount,
  addMonths,
  normName,
  approvedWorkingDays,
  checkAnnualAllowance,
  validateRosterWrite,
};
